import React from "react";
import { Form, ModalBuilder, Modal } from "@redriver/cinnamon";
import { updateNotificationSetting } from "./actions";
import BellInactiveIcon from "assets/icons/bell-inactive.svg";
import BellActiveIcon from "assets/icons/bell-active.svg";
import {
  NotificationFrequency,
  NotificationFrequencyLabels,
  NotificationActivity,
  NotificationActivityLabels,
  FileNotificationActivities,
  InheritNotificationFrequency,
} from "constants/enums";
import classNames from "classnames";

const activityOptions = (isFile) =>
  (isFile
    ? FileNotificationActivities
    : Object.values(NotificationActivity)
  ).map((v) => ({ text: NotificationActivityLabels[v], value: v }));

// carry across whatever is already in force, falling back to the default of every applicable activity
const initialActivities = (item, isFile, inheritedActivities) => {
  const applicable = activityOptions(isFile).map((x) => x.value);
  const inForce = item.notificationFrequency
    ? item.notificationActivities
    : inheritedActivities;
  const chosen = (inForce || []).filter((x) => applicable.includes(x));
  return chosen.length ? chosen : applicable;
};

const frequencyOptions = (inheritedFrequency, canInherit) => [
  ...(canInherit
    ? [
        {
          text: inheritedFrequency
            ? `Inherit from parent (${NotificationFrequencyLabels[inheritedFrequency]})`
            : "Inherit from parent (None)",
          value: InheritNotificationFrequency,
        },
      ]
    : []),
  ...Object.values(NotificationFrequency).map((v) => ({
    text: NotificationFrequencyLabels[v],
    value: v,
  })),
];

const NotificationLevelPicker = ({
  item,
  isFile,
  inheritedFrequency,
  inheritedActivities,
  onRefresh,
  hidden,
  canInherit = true, // false at the top of a site — its folder has no parent to inherit from
}) => {
  const effectiveFrequency = item.notificationFrequency || inheritedFrequency;
  const subscribed =
    !!effectiveFrequency && effectiveFrequency != NotificationFrequency.Never;

  return (
    <ModalBuilder
      withForm
      submitAction={updateNotificationSetting}
      initialData={{
        frequency:
          item.notificationFrequency ||
          (canInherit
            ? InheritNotificationFrequency
            : NotificationFrequency.Never),
        activities: initialActivities(item, isFile, inheritedActivities),
      }}
      submitParams={{
        directoryId: isFile ? null : item.id,
        fileId: isFile ? item.id : null,
      }}
      onSubmitted={() => onRefresh && onRefresh()}
      renderTrigger={(showModal) => (
        <img
          src={subscribed ? BellActiveIcon : BellInactiveIcon}
          className={classNames(
            "notification-level-picker",
            "pointer",
            hidden ? "hidden" : null
          )}
          onClick={showModal}
        />
      )}
      renderModal={(modalProps, formProps) => (
        <Modal
          {...modalProps}
          header="Notification Settings"
          submitLabel="Save"
          size="tiny"
        >
          <Form {...formProps}>
            <Form.RadioGroup
              field="frequency"
              label="How often should we notify you?"
              options={frequencyOptions(inheritedFrequency, canInherit)}
            />
            <Form.If
              condition={({ fields }) =>
                fields.frequency != InheritNotificationFrequency &&
                fields.frequency != NotificationFrequency.Never
              }
            >
              <Form.CheckboxGroup
                field="activities"
                label="What should we notify you about?"
                options={activityOptions(isFile)}
                required
              />
            </Form.If>
          </Form>
          <p>
            {isFile
              ? "This applies to this file only, and overrides the setting on the folder it sits in."
              : "This applies to everything inside this folder, and can be overridden on any folder or file below it."}
          </p>
        </Modal>
      )}
    />
  );
};

export default NotificationLevelPicker;
